import classNames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';

import Theme from 'styles/theme';
import I18n from 'utils/I18n';

import { DIRECTIONS, ARROW_SIZES } from './constants';

export { DIRECTIONS, ARROW_SIZES };

const BackArrow = props => {
  const { direction, size, onClick, children, disabled, className, ...otherProps } = props;
  const { BUTTON_LENGTH, ARROW_LENGTH, LINE_LENGTH } = size;

  const backArrowClass = classNames('BackArrow', className, {
    'BackArrow--left': direction === DIRECTIONS.LEFT,
    'BackArrow--right': direction === DIRECTIONS.RIGHT,
    'BackArrow--disabled': disabled,
  });

  return (
    <button className={backArrowClass} onClick={onClick} disabled={disabled} {...otherProps}>
      {children || I18n.localize('app.goBack')}
      <style jsx>
        {`
          .BackArrow {
            position: relative;
            display: block;
            appearance: none;
            border: none;
            text-align: center;
            padding: 0;
            margin: 0;
            text-indent: -9999em;
            width: ${BUTTON_LENGTH}px;
            height: ${BUTTON_LENGTH}px;
            min-width: ${BUTTON_LENGTH}px;
            background-color: ${Theme.get('colors-white')};
            outline: none;
          }

          .BackArrow.focused {
            outline: 2px solid ${Theme.get('colors-black')};
          }

          .BackArrow:hover {
            cursor: pointer;
          }

          .BackArrow:before,
          .BackArrow:after {
            position: absolute;
            content: '';
            display: block;
            top: 50%;
            transition: all ${Theme.get('transition-duration-ms')}
              ${Theme.get('transition-timing-ease-out')};
          }

          .BackArrow:after {
            transform: translateY(-50%);
            height: ${LINE_LENGTH}px;
            width: ${ARROW_LENGTH * 1.75}px;
            background-color: ${Theme.get('colors-black')};
          }

          .BackArrow:before {
            height: ${ARROW_LENGTH}px;
            width: ${ARROW_LENGTH}px;
            border-bottom: ${LINE_LENGTH}px solid ${Theme.get('colors-black')};
            border-left: ${LINE_LENGTH}px solid ${Theme.get('colors-black')};
            margin-right: ${LINE_LENGTH}px;
          }

          .BackArrow--left:before {
            transform: translateY(-50%) rotate(45deg);
          }

          .BackArrow--left:before,
          .BackArrow--left:after {
            left: ${ARROW_LENGTH * 0.5 + LINE_LENGTH}px;
          }

          .BackArrow--left:hover:before,
          .BackArrow--left:hover:after {
            left: ${LINE_LENGTH}px;
          }

          .BackArrow--right:before {
            transform: translateY(-50%) rotate(-135deg);
          }

          .BackArrow--right:before,
          .BackArrow--right:after {
            right: ${ARROW_LENGTH * 0.5 + LINE_LENGTH}px;
          }

          .BackArrow--right:hover:before,
          .BackArrow--right:hover:after {
            right: ${LINE_LENGTH}px;
          }

          .BackArrow--disabled.BackArrow:hover {
            cursor: default;
          }

          .BackArrow--disabled.BackArrow:before,
          .BackArrow--disabled.BackArrow:after {
            opacity: 0.28;
          }

          .BackArrow--disabled.BackArrow--left:hover:before,
          .BackArrow--disabled.BackArrow--left:hover:after {
            left: ${ARROW_LENGTH * 0.5 + LINE_LENGTH}px;
          }

          .BackArrow--disabled.BackArrow--right:hover:before,
          .BackArrow--disabled.BackArrow--right:hover:after {
            right: ${ARROW_LENGTH * 0.5 + LINE_LENGTH}px;
          }
        `}
      </style>
    </button>
  );
};

BackArrow.propTypes = {
  direction: PropTypes.oneOf(Object.values(DIRECTIONS)),
  size: PropTypes.oneOf(Object.values(ARROW_SIZES)),
  className: PropTypes.string,
  disabled: PropTypes.bool,
  onClick: PropTypes.func,
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
};

BackArrow.defaultProps = {
  direction: DIRECTIONS.LEFT,
  size: ARROW_SIZES.DEFAULT,
  disabled: false,
  className: '',
  onClick() {},
  children: null,
};

export default BackArrow;
